import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

export const MDLetterList = () => {
  const [letters, setLetters] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // ✅ Fetch letters based on selected filter (pending/signed/all)
  useEffect(() => {
    setLoading(true);

    const q =
      filter === "all"
        ? query(collection(db, "letters"), orderBy("date", "desc"))
        : query(
            collection(db, "letters"),
            where("status", "==", filter),
            orderBy("date", "desc")
          );

    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLetters(data);
      setLoading(false);
    });

    return () => unsub();
  }, [filter]);

  /* -----------------------------
     🔹 Loading & Empty States
  ----------------------------- */
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
        ⏳ Loading letters for review...
      </div>
    );
  }

  if (letters.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2 style={{ color: "#222" }}>📄 No Letters Found</h2>
        <p style={{ color: "#777" }}>
          No {filter === "pending" ? "pending" : filter} letters available.
        </p>
      </div>
    );
  }

  /* -----------------------------
     🔹 Style Constants
  ----------------------------- */
  const cardStyle = {
    border: "1px solid #e5e5e5",
    borderRadius: "12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
    padding: "20px 24px",
    marginBottom: "18px",
    background: "#fff",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  };

  const hoverStyle = {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
  };

  const statusColors = {
    pending: "#f39c12",
    signed: "#27ae60",
    rejected: "#c0392b",
  };

  const getStatusBadge = (status) => (
    <span
      style={{
        backgroundColor: statusColors[status] || "#555",
        color: "white",
        fontSize: "12px",
        padding: "3px 8px",
        borderRadius: "5px",
        textTransform: "capitalize",
      }}
    >
      {status}
    </span>
  );

  /* -----------------------------
     🔹 Render UI
  ----------------------------- */
  return (
    <div style={{ padding: "30px 20px", maxWidth: "1000px", margin: "0 auto" }}>
      {/* Header */}
      <h2
        style={{
          marginBottom: "25px",
          color: "#1a1a1a",
          textAlign: "center",
          fontWeight: 700,
          fontSize: "24px",
        }}
      >
        🖋️ Letter Review & Signature Dashboard
      </h2>

      {/* Filter Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "15px",
          marginBottom: "25px",
        }}
      >
        {["pending", "signed", "all"].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              background: filter === tab ? "#1976d2" : "#f5f5f5",
              color: filter === tab ? "#fff" : "#333",
              cursor: "pointer",
              fontWeight: 600,
              transition: "0.2s",
            }}
          >
            {tab === "pending"
              ? "⏳ Pending"
              : tab === "signed"
              ? "✅ Signed"
              : "📄 All"}
          </button>
        ))}
      </div>

      {/* Letter Cards */}
      {letters.map((l) => (
        <div
          key={l.id}
          style={cardStyle}
          onMouseEnter={(e) =>
            Object.assign(e.currentTarget.style, hoverStyle)
          }
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, cardStyle)}
        >
          {/* Header Row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              marginBottom: "8px",
            }}
          >
            <div>
              <strong style={{ fontSize: "15px", color: "#2c3e50" }}>
                {l.companyName}
              </strong>
              <div style={{ fontSize: "13px", color: "#555" }}>
                Ref: {l.referenceNo || "-"}
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "13px", color: "#777" }}>
                {new Date(l.date).toLocaleDateString("en-GB")}
              </div>
              <div style={{ marginTop: "4px" }}>
                {getStatusBadge(l.status || "pending")}
              </div>
            </div>
          </div>

          {/* Subject */}
          <h4 style={{ margin: "10px 0", color: "#1976d2" }}>{l.subject}</h4>

          {/* Body Preview */}
          <div
            style={{
              color: "#444",
              fontSize: "14px",
              marginBottom: "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
            dangerouslySetInnerHTML={{ __html: l.body }}
          />

          {/* Footer Buttons */}
          <div style={{ textAlign: "right" }}>
            {l.status === "pending" ? (
              <button
                onClick={() => navigate(`/sign-letter/${l.id}`)}
                style={{
                  background: "#1976d2",
                  color: "#fff",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  transition: "0.2s",
                }}
              >
                ✍️ Review & Sign
              </button>
            ) : (
              <button
                onClick={() => navigate(`/letters/view/${l.id}`)}
                style={{
                  background: "#2ecc71",
                  color: "#fff",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                📄 View Signed Letter
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
